import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import LockIcon from '@mui/icons-material/Lock';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

import { NoColorLink } from 'ui/NoColorLink';
import { useFetchActiveDashboards } from 'routes/AllDashboards/hooks/getDashboardsList';
import { NavLinkProps } from './helpers/types';
import StyButton from './StyButton';

const StyDisabled = styled(Box, {
    name: 'StyDisabled',
})(({ theme }) => ({
    alignItems: 'center',
    color: theme.palette.lightGrey,
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
}));

const InternalLink = ({
    children,
    url,
    disabled,
    backgroundColor,
    fontColor,
}: NavLinkProps) => {
    const { t } = useTranslation();
    const { href, pathname } = new URL(url);

    const { data: userDashboards } = useFetchActiveDashboards();
    const [isDisabled, setIsDisabled] = useState(disabled);

    useEffect(() => {
        const splittedUrl = pathname.split('/');
        if (splittedUrl[1] === 'dashboard') {
            const dashboard = userDashboards?.find(
                // If the provided URL used in the link starts with /dashboards,
                // we look for the corresponding dashboard slug in the URL.
                // If this dashboard does not exist, we disable this internal link.
                (d) => d.slug === splittedUrl[2],
            );
            setIsDisabled(!dashboard);
        } else {
            setIsDisabled(disabled);
        }
    }, [disabled, pathname, userDashboards]);

    if (isDisabled) {
        return (
            <Tooltip title={t('widget.navlink.noAccessToDasboard')}>
                <StyDisabled sx={{ backgroundColor }}>
                    <LockIcon
                        sx={{ ml: '20px', color: fontColor }}
                        fontSize="small"
                    />
                    {children}
                </StyDisabled>
            </Tooltip>
        );
    }
    return (
        <StyButton
            component={NoColorLink}
            sx={{ justifyContent: 'center' }}
            backgroundColor={backgroundColor}
            to={href}
        >
            {children}
        </StyButton>
    );
};

export default React.memo(InternalLink);
